import React, { useState, useEffect, useRef } from 'react';
import { tourSteps } from './tourSteps';
import { useAuth } from '../../context/AuthContext';
import { X } from 'lucide-react';
import { Card, CardContent } from "@vaiu/ui/card";

const AppTour: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();

  useEffect(() => {
    if (user && !hasSeenTour) {
      const tourNeverShow = localStorage.getItem(`tour_never_show_${user._id}`);
      
      if (!tourNeverShow) {
        // Show tour prompt after a brief delay
        setTimeout(() => {
          setShowPrompt(true);
        }, 2000);
      }
    }
  }, [user, hasSeenTour]);

  // Listen for manual tour trigger events
  useEffect(() => {
    const handleStartTour = () => {
      if (user) {
        startTour();
      }
    };

    window.addEventListener('startAppTour', handleStartTour);
    return () => window.removeEventListener('startAppTour', handleStartTour);
  }, [user]);

  const startTour = () => {
    setShowPrompt(false);
    setIsActive(true);
    setCurrentStep(0);
    setHasSeenTour(true);
    if (user) {
      localStorage.setItem(`tour_shown_${user._id}`, 'true');
    }
    
    // Hide any existing UI elements that might interfere
    document.body.style.overflow = 'hidden';
    
    // Start with first step
    highlightElement(tourSteps[0]);
  };

  const skipTour = () => {
    setShowPrompt(false);
    // Don't set hasSeenTour or localStorage items for "Maybe later"
  };

  const neverShowTourAgain = () => {
    setShowPrompt(false);
    setHasSeenTour(true);
    if (user) {
      localStorage.setItem(`tour_shown_${user._id}`, 'true');
      localStorage.setItem(`tour_never_show_${user._id}`, 'true');
      localStorage.setItem(`tour_skipped_${user._id}`, 'true');
    }
  };

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      highlightElement(tourSteps[currentStep + 1]);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      highlightElement(tourSteps[currentStep - 1]);
    }
  };

  const completeTour = () => {
    setIsActive(false);
    setTourCompleted(true);
    setCurrentStep(0);
    document.body.style.overflow = 'auto';
    
    if (user) {
      localStorage.setItem(`tour_completed_${user._id}`, 'true');
    }
    
    // Show completion message
    setTimeout(() => {
      setTourCompleted(false);
    }, 3000);
  };

  const highlightElement = (step: typeof tourSteps[0]) => {
    const element = document.querySelector(step.selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // Update overlay to highlight the element
      if (overlayRef.current) {
        overlayRef.current.style.clipPath = `polygon(
          0% 0%, 
          0% 100%, 
          ${rect.left + scrollLeft}px 100%, 
          ${rect.left + scrollLeft}px ${rect.top + scrollTop}px, 
          ${rect.right + scrollLeft}px ${rect.top + scrollTop}px, 
          ${rect.right + scrollLeft}px ${rect.bottom + scrollTop}px, 
          ${rect.left + scrollLeft}px ${rect.bottom + scrollTop}px, 
          ${rect.left + scrollLeft}px 100%, 
          100% 100%, 
          100% 0%
        )`;
      }

      // Position tooltip
      if (tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        let top = rect.bottom + scrollTop + 10;
        let left = rect.left + scrollLeft + (rect.width / 2) - (tooltipRect.width / 2);

        // Adjust if tooltip goes off screen
        if (left < 10) left = 10;
        if (left + tooltipRect.width > window.innerWidth - 10) {
          left = window.innerWidth - tooltipRect.width - 10;
        }
        if (top + tooltipRect.height > window.innerHeight + scrollTop - 10) {
          top = rect.top + scrollTop - tooltipRect.height - 10;
        }

        tooltipRef.current.style.top = `${top}px`;
        tooltipRef.current.style.left = `${left}px`;
      }

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add pulse animation to highlighted element
      element.classList.add('tour-highlight');
      setTimeout(() => {
        element.classList.remove('tour-highlight');
      }, 2000);
    }
  };

  const restartTour = () => {
    setIsActive(true);
    setCurrentStep(0);
    setTourCompleted(false);
    highlightElement(tourSteps[0]);
  };

  if (!user) return null;

  const currentStepData = tourSteps[currentStep];

  // Don't render anything if not showing prompt or tour
  if (!showPrompt && !isActive && !tourCompleted) {
    return null;
  }

  return (
    <>
      {/* Tour Prompt Banner */}
      {showPrompt && (
        <Card className="bg-white shadow-sm border">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 overflow-hidden">
              <div className="p-8">
                <div className="flex flex-col items-center text-center gap-6">
                  <div className="flex flex-col items-center gap-4 w-full">
                    <button
                      onClick={startTour}
                      className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white text-base font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      Start Tour
                    </button>
                    <div className="flex items-center gap-6 text-xs text-blue-600">
                      <button
                        onClick={neverShowTourAgain}
                        className="hover:text-blue-800 transition-colors"
                      >
                        Don't show again
                      </button>
                      <button
                        onClick={skipTour}
                        className="hover:text-blue-800 transition-colors"
                      >
                        Maybe later
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tour Overlay and Tooltip */}
      {isActive && (
        <>
          <div ref={overlayRef} className="tour-overlay" />
          <div ref={tooltipRef} className="tour-tooltip">
            <div className="tour-tooltip-content">
              <div className="tour-step-counter">
                Step {currentStep + 1} of {tourSteps.length}
              </div>
              <h3>{currentStepData.title}</h3>
              <p>{currentStepData.description}</p>
              
              {/* TODO: Add feature demonstration GIFs */}
              {/* {currentStepData.gif && (
                <div className="tour-gif-container">
                  <img src={currentStepData.gif} alt="Feature demonstration" />
                </div>
              )} */}
              
              {currentStepData.tips && (
                <div className="tour-tips">
                  <h4>💡 Pro Tips:</h4>
                  <ul>
                    {currentStepData.tips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="tour-navigation">
              <button 
                onClick={prevStep} 
                disabled={currentStep === 0}
                className="tour-btn-nav"
              >
                Previous
              </button>
              
              <button 
                onClick={() => {
                  setIsActive(false);
                  document.body.style.overflow = 'auto';
                }}
                className="tour-btn-skip"
              >
                Skip Tour
              </button>
              
              <button 
                onClick={nextStep}
                className="tour-btn-nav tour-btn-primary"
              >
                {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Tour Completed Message */}
      {tourCompleted && (
        <div className="tour-completed-message">
          <div className="tour-completed-content">
            <h2>🎉 Tour Completed!</h2>
            <p>You're all set to explore Vaiu. Enjoy using the app!</p>
            <button onClick={restartTour} className="tour-btn-secondary">
              Take Tour Again
            </button>
          </div>
        </div>
      )}

      {/* Tour Progress Bar */}
      {isActive && (
        <div className="tour-progress-bar">
          <div 
            className="tour-progress-fill" 
            style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
          />
        </div>
      )}
    </>
  );
};

export default AppTour; 